import { Flex, Grid, Icon, VisuallyHidden } from "@daangn/carotene";
import { breakpoints } from "@daangn/carotene/css";
import { IconChevronLeftLine } from "@daangn/react-monochrome-icon";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import clsx from "clsx";
import { useEffect, useRef, useState } from "react";

import { useDisclosure } from "~/hooks/useDisclosure";
import { transformImageUrl } from "~/utils/transformImageUrl";

import Image from "./Image";
import * as css from "./ImageGalleryDialog.css";
import type { ImageSlide } from "./Lightbox";
import Lightbox from "./Lightbox";

type ImageGalleryDialogProps = {
  slides: ImageSlide[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

export function ImageGalleryDialog({
  slides,
  open,
  onOpenChange,
}: ImageGalleryDialogProps) {
  const lightboxPortalRef = useRef<HTMLDivElement>(null);
  const [currentLightboxSlide, setCurrentLightboxSlide] = useState(0);
  const {
    isOpen: isLightboxDialogOpen,
    onOpen: onLightboxDialogOpen,
    onClose: onLightboxDialogClose,
    setOpenState: setLightboxDialogOpenState,
  } = useDisclosure();
  const {
    isOpen: isLightboxOpen,
    onOpen: onLightboxOpen,
    onClose: onLightboxClose,
  } = useDisclosure();

  const [firstSlide, ...restSlides] = slides.map((slide, index) => ({
    ...slide,
    onClick: () => {
      setCurrentLightboxSlide(index);
      onLightboxDialogOpen();
    },
  }));

  useEffect(() => {
    if (isLightboxDialogOpen) {
      onLightboxOpen();
    }
  }, [isLightboxDialogOpen, onLightboxOpen]);

  return (
    <>
      <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
        <DialogPrimitive.Overlay />
        <DialogPrimitive.Portal>
          <DialogPrimitive.Content className={css.imageGalleryModalContent}>
            <Flex
              as="header"
              padding={2}
              backgroundColor="layerDefault"
              alignItems="center"
              className={css.header}
            >
              <DialogPrimitive.Close className={css.close}>
                <Icon
                  svg={<IconChevronLeftLine />}
                  size={6}
                  color="neutral"
                  aria-hidden
                />
                <VisuallyHidden>닫기</VisuallyHidden>
              </DialogPrimitive.Close>
            </Flex>
            <section className={css.imageGalleryModalSection}>
              <button
                type="button"
                key={firstSlide.src}
                onClick={firstSlide.onClick}
                className={css.galleryFirstImageButton}
              >
                <Image
                  {...makeResponsiveFirstImageProps(firstSlide.src)}
                  alt={firstSlide.alt ?? ""}
                  wrapperClassName={css.galleryFirstImageWrapper}
                  imgClassName={css.galleryFirstImage}
                />
              </button>
              <Grid gap={{ base: 1, small: 2 }} columns={{ base: 1, small: 2 }}>
                {restSlides.map((slide) => (
                  <button
                    type="button"
                    key={slide.src}
                    onClick={slide.onClick}
                    className={clsx(css.galleryRestImageWrapper, css.pointer)}
                  >
                    <Image
                      {...makeResponsiveRestImageProps(slide.src)}
                      alt={slide.alt ?? ""}
                      wrapperClassName={css.galleryRestImageWrapper}
                      imgClassName={css.galleryRestImage}
                    />
                  </button>
                ))}
              </Grid>
            </section>
          </DialogPrimitive.Content>
        </DialogPrimitive.Portal>
      </DialogPrimitive.Root>
      {/* NOTE: 이미지 갤러리를 위한 Dialog 내부에 Lightbox를 렌더링하면 포커스가 Lightbox로 좁혀지지 않고
    Dialog 범위까지 가능한 문제가 있어요. 그래서 Lightbox를 또 다른 Dialog에 렌더링해요. */}
      <DialogPrimitive.Root
        open={isLightboxDialogOpen}
        onOpenChange={setLightboxDialogOpenState}
      >
        <DialogPrimitive.Overlay />
        <DialogPrimitive.Portal>
          <DialogPrimitive.Content
            onEscapeKeyDown={(e) => {
              if (isLightboxOpen) {
                e.preventDefault();
              }
            }}
            className={css.lightboxModalContent}
            /**
             * NOTE: Dialog가 열려있으면 Dialog 외부 요소에서 발생하는 이벤트와 포커스를 막아서
             * Lightbox가 동작하지 않는 문제가 있어요. 그래서 Lightbox를 Dialog 자식 요소로
             * 렌더링해요.
             */
            ref={lightboxPortalRef}
          >
            {lightboxPortalRef.current && (
              <Lightbox
                index={currentLightboxSlide}
                open={isLightboxOpen}
                close={() => {
                  onLightboxClose();
                  onLightboxDialogClose();
                }}
                slides={slides}
                portal={{ root: lightboxPortalRef.current }}
                on={{ view: ({ index }) => setCurrentLightboxSlide(index) }}
                noScroll={{ disabled: true }}
              />
            )}
          </DialogPrimitive.Content>
        </DialogPrimitive.Portal>
      </DialogPrimitive.Root>
    </>
  );
}

function makeResponsiveFirstImageProps(imageUrl: string) {
  return {
    srcSet: [
      `${transformImageUrl(imageUrl, {
        width: 300,
        height: 300,
        quality: 82,
        cropStrategy: "crop",
      })} 300w`,
      `${transformImageUrl(imageUrl, {
        width: 720,
        height: 720,
        quality: 95,
        cropStrategy: "inside",
      })} 720w`,
    ].join(", "),
    sizes: "100vw",
    src: imageUrl,
  };
}

function makeResponsiveRestImageProps(imageUrl: string) {
  return {
    srcSet: [
      `${transformImageUrl(imageUrl, {
        width: 300,
        height: 300,
        quality: 82,
        cropStrategy: "crop",
      })} 300w`,
      `${transformImageUrl(imageUrl, {
        width: 720,
        height: 720,
        quality: 95,
        cropStrategy: "inside",
      })} 720w`,
    ].join(", "),
    sizes: `(min-width: ${breakpoints.small}px) 50vw, 100vw`,
    src: imageUrl,
  };
}
